import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Button } from './Button'

/* ------------------------------------------------------------------
   اجزای مشترکِ «صفحهٔ جدول»
   ------------------------------------------------------------------
   🔴 چرا این فایل ساخته شد.

   ممیزیِ ۲۰ صفحهٔ جدول‌دار نشان داد خودِ `DataTable` از قبل مشترک است،
   ولی هر چیزی که **دورِ** آن است دستی و تکراری ساخته شده:

     · نوار فیلتر  — ۱۰ صفحه، هر بار یک `Card>CardBody>grid` دست‌ساز
     · ردیف اقدام  — ۱۵ صفحه، همان رشتهٔ کلاسِ `justify-end`
     · سلولِ کد    — ۱۳ صفحه، همان `font-mono text-sm`
     · سلولِ دوخطی — ۵ صفحه با **سه** پیاده‌سازیِ متفاوت
     · سلولِ اقدام — ۹ صفحه با دو شکلِ ناسازگار

   نتیجهٔ عملیِ این پراکندگی فقط تکرارِ کد نبود؛ ناهمگونیِ دیده‌شدنی بود:
   سلولِ دوخطی در سه ماژول با سه اندازه و سه رنگِ متفاوت رندر می‌شد
   (`--pf-text-muted` / `--pf-text-subtle` / `--pf-text-2xs`).

   ⚠️ این فایل عمداً «کامپوننتِ جدول» نمی‌سازد؛ `DataTable` سرِ جایش
   است. اینجا فقط قطعاتِ **اطرافِ** جدول و **داخلِ سلول** جمع شده‌اند.
   ------------------------------------------------------------------ */

/* ==================================================================
   نوار ابزار جدول — جست‌وجو و فیلتر
   ================================================================== */

export interface TableToolbarProps {
  /** فیلدهای فیلتر — معمولاً چند `<Field>` */
  children: ReactNode
  /**
   * تعداد ستون در دسکتاپ. موبایل همیشه تک‌ستونی است.
   *
   * ⚠️ عددها به‌صورت کلاسِ کامل نوشته می‌شوند و نه با درج رشته‌ای
   * (`sm:grid-cols-${cols}`)؛ Tailwind کلاس‌ها را با اسکنِ ایستا پیدا
   * می‌کند و کلاسِ ساخته‌شده در زمان اجرا هرگز در خروجی نمی‌آید.
   */
  columns?: 1 | 2 | 3 | 4
  /** اقدام‌های سمتِ پایان نوار — مثل «پاک‌کردن فیلترها» */
  actions?: ReactNode
  /** شمارِ نتیجه — وقتی فیلتری فعال است کمک می‌کند */
  resultCount?: ReactNode
  className?: string
}

const TOOLBAR_COLUMNS: Record<1 | 2 | 3 | 4, string> = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
}

/**
 * نوار جست‌وجو و فیلترِ بالای جدول.
 *
 * ⚠️ خودش `Card` نیست و کارت هم نمی‌سازد: در صفحه‌هایی که جدول داخل
 * کارت است، نوار باید **داخلِ همان کارت** بنشیند تا دو ظرفِ پشت سر هم
 * ساخته نشود. صفحه تصمیم می‌گیرد کجا بگذاردش.
 */
export function TableToolbar({
  children,
  columns = 3,
  actions,
  resultCount,
  className,
}: TableToolbarProps) {
  return (
    <div
      className={cn(
        'border-b border-[var(--pf-border)] bg-[var(--pf-surface)]',
        'px-[var(--pf-space-4)] py-[var(--pf-space-4)]',
        className,
      )}
    >
      <div className={cn('grid gap-[var(--pf-space-3)]', TOOLBAR_COLUMNS[columns])}>
        {children}
      </div>

      {/*
        ⚠️ ردیفِ پایینی خطِ جداکننده دارد و نه فقط فاصله.

        در آزمون مرورگر، «۰ نتیجه» و «پاک‌کردن فیلترها» بدون مرز زیرِ
        فیلدها شناور بودند و معلوم نبود به فیلترها مربوط‌اند یا به
        جدول. خطِ نازک آن‌ها را به نوارِ فیلتر می‌چسباند.
      */}
      {(actions || resultCount) && (
        <div
          className={cn(
            'mt-[var(--pf-space-3)] flex flex-wrap items-center gap-[var(--pf-space-2)]',
            'border-t border-[var(--pf-border)] pt-[var(--pf-space-3)]',
            /* وقتی شمارنده نیست، اقدام‌ها به لبهٔ پایان می‌روند. */
            resultCount ? 'justify-between' : 'justify-end',
          )}
        >
          {resultCount && (
            <span className="text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
              {resultCount}
            </span>
          )}
          {actions && <div className="flex items-center gap-[var(--pf-space-2)]">{actions}</div>}
        </div>
      )}
    </div>
  )
}

/* ==================================================================
   دکمهٔ فیلترِ دوحالته
   ================================================================== */

export interface ToggleFilterProps {
  active: boolean
  onChange: (next: boolean) => void
  children: ReactNode
  className?: string
}

/**
 * فیلترِ روشن/خاموش — مثل «فقط بازها».
 *
 * ⚠️ `aria-pressed` می‌گیرد و نه فقط رنگِ متفاوت: کاربر صفحه‌خوان باید
 * بفهمد این دکمه یک کلیدِ دوحالته است نه یک اقدامِ معمولی. پیش‌تر هفت
 * صفحه این را با `variant={x ? 'primary' : 'secondary'}` می‌ساختند که
 * فقط بصری بود.
 */
export function ToggleFilter({ active, onChange, children, className }: ToggleFilterProps) {
  return (
    <Button
      variant={active ? 'primary' : 'secondary'}
      aria-pressed={active}
      onClick={() => onChange(!active)}
      className={className}
    >
      {children}
    </Button>
  )
}

/* ==================================================================
   سلول‌های جدول
   ================================================================== */

export interface TwoLineCellProps {
  /** خط اول — دادهٔ اصلی */
  primary: ReactNode
  /** خط دوم — دادهٔ فرعی؛ اگر تهی باشد اصلاً رندر نمی‌شود */
  secondary?: ReactNode
  /** خط دوم مونواسپیس شود — برای ایمیل، کد و شناسه */
  mono?: boolean
  className?: string
}

/**
 * سلولِ «اصلی + فرعی» — نام و واحد، تاریخ و ساعت، نام و ایمیل.
 *
 * 🔴 پیش‌تر پنج صفحه این را با سه پیاده‌سازیِ متفاوت می‌ساختند:
 * یکی `--pf-text-muted`، یکی `--pf-text-subtle` و یکی
 * `--pf-text-2xs` — یعنی یک الگوی واحد در سه ماژول سه اندازه و رنگ
 * داشت. حالا یک منبع دارد.
 *
 * ⚠️ خط دوم `truncate` می‌شود ولی خط اول نه: خط اول شناسهٔ ردیف است و
 * بریدنش دو ردیفِ متفاوت را غیرقابل‌تفکیک می‌کند.
 */
export function TwoLineCell({ primary, secondary, mono, className }: TwoLineCellProps) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[var(--pf-text)]">{primary}</div>

      {secondary !== null && secondary !== undefined && secondary !== '' && (
        <div
          className={cn(
            'truncate text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]',
            mono && 'font-mono',
          )}
        >
          {secondary}
        </div>
      )}
    </div>
  )
}

export interface CodeCellProps {
  children: ReactNode
  className?: string
}

/**
 * شمارهٔ رکورد — همیشه مونواسپیس.
 *
 * ⚠️ مونواسپیس تزئین نیست: شماره‌هایی مثل `LV-2026-00002` را کاربر
 * رقم‌به‌رقم با هم مقایسه می‌کند و با فونتِ متغیر، ارقام هم‌عرض نیستند
 * و ستون دندانه‌دار می‌شود.
 */
export function CodeCell({ children, className }: CodeCellProps) {
  return (
    <span
      className={cn(
        'font-mono text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]',
        className,
      )}
    >
      {children}
    </span>
  )
}

export interface RowActionsProps {
  children: ReactNode
  className?: string
}

/**
 * ظرفِ دکمه‌های اقدامِ یک ردیف.
 *
 * ⚠️ همیشه به لبهٔ پایانِ سلول می‌چسبد. پیش‌تر نُه صفحه این را با دو
 * شکلِ ناسازگار می‌ساختند (`justify-end gap-2` در پنج صفحه و
 * `flex-wrap gap-1` در سه صفحه) و ستونِ اقدام در ماژول‌های مختلف
 * هم‌تراز نبود.
 *
 * ⚠️ `stopPropagation` اینجاست و نه در هر دکمه: در جدولی که
 * `onRowClick` دارد، کلیک روی دکمهٔ داخلِ ردیف نباید هم‌زمان ردیف را
 * هم باز کند. پیش‌تر هر صفحه باید یادش می‌ماند و یکی‌شان یادش رفته بود.
 */
export function RowActions({ children, className }: RowActionsProps) {
  return (
    <div
      onClick={(event) => event.stopPropagation()}
      className={cn('flex items-center justify-end gap-[var(--pf-space-2)]', className)}
    >
      {children}
    </div>
  )
}

/* ==================================================================
   خطای جدول
   ================================================================== */

export interface TableError {
  message?: string
  requestId?: string | null
}

/**
 * تبدیل خطای react-query به شکلی که `DataTable` می‌فهمد.
 *
 * 🔴 چرا لازم شد: ده صفحه این را دستی می‌نوشتند و
 * `{ message: (error as Error).message }` می‌دادند — یعنی `requestId`
 * را می‌انداختند. `ErrorState` آن کد را نشان می‌دهد و کاربر برای پیگیری
 * به پشتیبانی همان را می‌گوید؛ نبودنش یعنی پیگیری غیرممکن.
 *
 * ⚠️ فقط `isError` کافی نیست: `error` می‌تواند از یک fetchِ قبلی مانده
 * باشد در حالی که کوئری الان موفق است.
 */
export function toTableError(query: {
  isError: boolean
  error: unknown
}): TableError | null {
  if (!query.isError || !query.error) return null

  const error = query.error as { message?: string; requestId?: string | null }

  return {
    message: error.message,
    requestId: error.requestId ?? null,
  }
}
